import { useCallback, useEffect, useRef, useState } from 'react';
import { CLOUD_RUN_BASE_URL } from 'core/constants';
import { getIdToken } from 'core/util';

/**
 * Structured product info returned by the avid-warranty-cloud
 * `/analyze_product_image` endpoint (the `.response` object of its envelope).
 * Fields the model can't read from the label come back as an empty string or
 * the literal `"Not Found"` — callers should treat both as "no value".
 */
export interface AnalyzeProductImageResult {
  model_number: string;
  model_name: string;
  serial_number: string;
  manufacturer: string;
  model_year: string;
  model_type: string;
  model_series: string;
  model_variant: string;
  model_color: string;
  model_finish: string;
  product_manufacturer_name: string;
}

/**
 * The Cloud Run service always responds with HTTP 200 and carries the real
 * outcome in the envelope's `status_code`; `response` is the result object on
 * success or an error string / `{ message }` otherwise.
 */
interface CloudResponseEnvelope {
  status_code?: number;
  response?: AnalyzeProductImageResult | { message?: string } | string;
}

const EMPTY_VALUES = ['', 'not found'];

/**
 * Upper bound on the analyze request. The endpoint runs an LLM over the label
 * image, so it is legitimately slow (single-digit seconds) — this is a
 * hung-request backstop, not a latency target. Without it a request that never
 * settles leaves `isAnalyzing` true forever, which disables the modal's Cancel
 * button and makes Analyze a no-op, bricking the feature until the user
 * navigates away.
 */
const ANALYZE_TIMEOUT_MS = 60000;

/** Treats `""` and `"Not Found"` (case-insensitive) as no value, per the OCR spec. */
export const isEmptyOcrValue = (value?: string | null): boolean =>
  !value || EMPTY_VALUES.includes(value.trim().toLowerCase());

/**
 * Calls avid-warranty-cloud's product-label analysis endpoint directly from
 * the app, authenticated with the current user's Firebase ID token (the
 * cloud endpoint accepts a Firebase bearer alongside its shared secret).
 * Mirrors avid-warranty-admin's `useAnalyzeProductImage` — this is a plain
 * `fetch` rather than the app's `createApiRequest`/HomeKeep-JWT flow, because
 * it targets an external Cloud Run service using the raw Firebase token, not
 * the HomeKeep JWT `createApiRequest` attaches.
 *
 * Dependency: requires the `allow_firebase_auth=True` avid-warranty-cloud
 * change (RHC10-15263) to be deployed to the target environment. Until then
 * (or if `CLOUD_RUN_BASE_URL` is unset), `analyze` throws and callers should
 * degrade to manual entry rather than blocking the add/edit flow.
 */
export const useAnalyzeProductImage = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const isMountedRef = useRef(true);

  // Tracks mount state so the finally block below doesn't set state on an
  // unmounted component when the caller's screen closes mid-request.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const analyze = useCallback(
    async (productImageUrl: string): Promise<AnalyzeProductImageResult> => {
      if (!CLOUD_RUN_BASE_URL) {
        throw new Error(
          'Image analysis is not configured for this environment'
        );
      }

      const token = await getIdToken();
      if (!token) {
        throw new Error('You must be signed in to analyze an image');
      }

      setIsAnalyzing(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        ANALYZE_TIMEOUT_MS
      );
      try {
        let res: Response;
        try {
          res = await fetch(`${CLOUD_RUN_BASE_URL}/analyze_product_image`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ product_image_url: productImageUrl }),
            signal: controller.signal
          });
        } catch (error: unknown) {
          // Translate the abort into something the caller can show the user —
          // a bare AbortError reads as a crash, not a timeout.
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(
              'Image analysis took too long. Please try again or enter the details manually.'
            );
          }
          throw error;
        }

        const envelope = (await res
          .json()
          .catch(() => ({}))) as CloudResponseEnvelope;
        const status = envelope.status_code ?? res.status;

        if (
          !res.ok ||
          status !== 200 ||
          !envelope.response ||
          typeof envelope.response !== 'object'
        ) {
          const detail =
            envelope.response &&
            typeof envelope.response === 'object' &&
            'message' in envelope.response
              ? envelope.response.message
              : typeof envelope.response === 'string'
              ? envelope.response
              : undefined;
          throw new Error(detail || `Image analysis failed (status ${status})`);
        }

        return envelope.response as AnalyzeProductImageResult;
      } finally {
        clearTimeout(timeoutId);
        if (isMountedRef.current) {
          setIsAnalyzing(false);
        }
      }
    },
    []
  );

  return { analyze, isAnalyzing };
};
